import './modal.scss';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { createElementFromHTML, checkAndLoadFiles } from './../../utilities/js/helper';

class Modal {
    constructor (element, options) {
        const defaults = {
            initAttr: 'data-modal',
            modalAttr: 'data-modaltarget',
            scrollerAttr: 'scroller',
            onAjaxLoaded: null,
            onClosed: null,
            loader: null
        };

        this.settings = Object.assign({}, defaults, options);
        this.$modal = element;
        this.modalOpen = false;
        this.$modalTarget = null;
        this.$modalScroller = null;
        this.waitingListID = null; // optional just for waiting list usage (pages -> seminar -> details.html.twig / templates -> components -> termin-teaser -> includes)
        this.$loadingSpinner = createElementFromHTML(window.OPTIONS.loader);
        this.fetchUrl = this.$modal.dataset.modalContentUrl;
        this.fetchJson = this.$modal.dataset.modalContentJson;
        this.$modalContentBlob = null;

        const modalId = this.$modal.getAttribute('data-modal');

        if (modalId) {
            this.$modalTarget = document.getElementById(modalId);
            this.$modalContentBlob = this.$modalTarget.querySelector('[data-modal-content-blob]');

            if (modalId === 'waiting_list') {
                this.waitingListID = this.$modal.getAttribute('data-waiting-list-id');
            }
        }
    }

    initialize () {
        this.setEvents();
    }

    setEvents () {
        this.$modal.setAttribute('data-modal-init', 'true');

        this.$modal.addEventListener('click', (e) => {
            e.preventDefault();
            this.openModal();
        });

        if (this.$modal.getAttribute('data-modal-open') !== null) {
            window.setTimeout(() => {
                this.openModal();
            }, 500);
        }

        // open modal on enter or space key
        this.$modal.addEventListener('keydown', (e) => {
            if (e.code === 'Enter' || e.code === 'Space') {
                e.preventDefault();
                this.openModal();
            }
        });

        // close modal on ESC key
        document.addEventListener('keydown', (e) => {
            let isEscape = false;
            if ('key' in e) {
                isEscape = (e.key === 'Escape' || e.key === 'Esc');
            } else {
                isEscape = (e.keyCode === 27);
            }
            if (isEscape && this.modalOpen === true) {
                this.closeModal();
            }
        });
    }

    fetchContentFromJson () {
        this.$modalContentBlob.innerHTML = '';
        this.$modalContentBlob.innerHTML = JSON.parse(this.fetchJson);
        this.showModal();
    }

    fetchContentFromFetchUrl () {
        this.$modalContentBlob.innerHTML = '';
        this.$modalContentBlob.appendChild(this.$loadingSpinner);

        fetch(this.fetchUrl, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
            .then(response => {
                if (!response.ok && response.status !== 422) {
                    throw new Error(`HTTP ERROR: ${response.status}`);
                }
                return response.text();
            })
            .then(data => {
                if (data) {
                    const $modalContent = createElementFromHTML(data);
                    this.$modalContentBlob.innerHTML = $modalContent.innerHTML;

                    checkAndLoadFiles(this.$modalContentBlob).then((data) => {
                        window.dispatchEvent(new CustomEvent('content.loaded', {
                            view: window,
                            bubbles: true,
                            cancelable: false,
                            detail: {
                                $context: this.$modalContentBlob
                            }
                        }));

                        window.dispatchEvent(new CustomEvent('fetch.content', {
                            view: window,
                            bubbles: true,
                            cancelable: false,
                            detail: {
                                $context: this.$modalContentBlob
                            }
                        }));
                    });

                    this.showModal();
                }
            })
            .catch(error => console.error('ERROR:', error));
    }

    openModal () {
        this.$modal.setAttribute('aria-pressed', 'true');

        const modelOpen = new CustomEvent('modal.open', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: this.$modal
            }
        });

        window.dispatchEvent(modelOpen);

        if (this.fetchUrl !== undefined && this.$modalContentBlob !== undefined) {
            this.fetchContentFromFetchUrl();
        } else if (this.fetchJson !== undefined && this.$modalContentBlob !== undefined) {
            this.fetchContentFromJson();
        } else {
            if (this.$modalTarget) {
                this.showModal();
            }
        }
    }

    showModal () {
        this.$modalScroller = this.$modalTarget.querySelector('[' + this.settings.modalAttr + '="' + this.settings.scrollerAttr + '"]');
        this.$modalTarget.classList.add('is--active');

        if (this.waitingListID !== null) {
            window.addEventListener('fetch.content', () => {
                const $waitingListIDInput = this.$modalTarget.querySelector('[data-waiting-list-id-input]');

                if ($waitingListIDInput) {
                    $waitingListIDInput.setAttribute('data-waiting-list-id-input', this.waitingListID);
                }
            });
        }

        this.$modalTarget.querySelector('[data-modal-close-btn]').addEventListener('keydown', (e) => {
            if (e.code === 'Enter' || e.code === 'Space') {
                e.preventDefault();
                this.closeModal();
            }
        });

        setTimeout(() => {
            this.$modalTarget.querySelector('[data-modal-close-btn]').focus();
        }, 100);

        if (this.$modalScroller) {
            disableBodyScroll(this.$modalScroller);
        }

        document.addEventListener('keydown', (event) => {
            if (event.code === 'Tab' || event.code === 'Tabs') {
                this.handleOutsideClick(event);
            }
        });

        const $$exists = this.$modalTarget.querySelectorAll('[data-modal-close]');
        $$exists.forEach(($exit) => {
            $exit.addEventListener('click', (e) => {
                e.preventDefault();
                this.closeModal();
            });
        });

        this.modalOpen = true;
        this.$modalTarget.API = this;
    }

    closeModal () {
        this.$modalTarget.classList.remove('is--active');

        if (this.$modalScroller) {
            enableBodyScroll(this.$modalScroller);
        }

        this.modalOpen = false;

        if (typeof this.settings.onClosed === 'function') {
            this.settings.onClosed(this.$modalTarget);
        }

        if (this.$modal) {
            this.$modal.setAttribute('aria-pressed', 'false');

            setTimeout(() => {
                this.$modal.focus();
            }, 200);
        }

        const modelClosed = new CustomEvent('modal.closed', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: this.$modalTarget
            }
        });

        window.dispatchEvent(modelClosed);
    }

    handleOutsideClick (e) {
        if (!this.$modalTarget.contains(e.target) && this.modalOpen) {
            this.closeModal();
        }
    }
}

export { Modal };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$modal = $context.querySelectorAll('[data-modal]:not([data-modal-init="true"])');

        for (let i = 0; i < $$modal.length; i++) {
            const modal = new Modal($$modal[i]);
            modal.initialize();
        }
    }
});
